import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCurrentCompany } from './useCurrentCompany';

export interface TrialStudent {
  enrollment_id: string;
  user_id: string | null;
  child_id: string | null;
  student_name: string;
  subscription_name: string;
  contract_start_date: string | null;
  contract_end_date: string | null;
  status: string;
  classes: Array<{
    class_id: string;
    class_name: string;
    enrolled_at: string;
  }>;
}

export function useCurrentTrials() {
  const { data: companyId, isLoading: isLoadingCompany } = useCurrentCompany();

  return useQuery<TrialStudent[]>({
    queryKey: ['current-trials', companyId],
    enabled: !!companyId && !isLoadingCompany,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!companyId) {
        return [];
      }

      // Get all trial subscription IDs for this company
      const { data: trialSubscriptions, error: trialSubsError } = await supabase
        .from('company_subscriptions')
        .select('id, name')
        .eq('subscription_type', 'trial')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .eq('is_deleted', false);

      if (trialSubsError || !trialSubscriptions || trialSubscriptions.length === 0) {
        return [];
      }

      const trialSubscriptionIds = trialSubscriptions.map(s => s.id);
      const subscriptionNamesMap = new Map(
        trialSubscriptions.map(s => [s.id, s.name || 'Trial Subscription'])
      );

      // Get active trial enrollments
      const { data: trialEnrollments, error: enrollmentsError } = await supabase
        .from('subscription_enrollments')
        .select(`
          id,
          user_id,
          child_id,
          subscription_id,
          contract_start_date,
          contract_end_date,
          status,
          user_profiles!subscription_enrollments_user_id_fkey (
            first_name,
            last_name
          ),
          child_profiles!subscription_enrollments_child_id_fkey (
            first_name,
            last_name
          )
        `)
        .eq('company_id', companyId)
        .in('subscription_id', trialSubscriptionIds)
        .in('status', ['pending', 'pending_approval', 'active']);

      if (enrollmentsError || !trialEnrollments || trialEnrollments.length === 0) {
        return [];
      }

      // Get student IDs (both user_id and child_id)
      const studentIds: Array<{ id: string; type: 'user' | 'child' }> = [];
      trialEnrollments.forEach((enrollment: any) => {
        if (enrollment.user_id && !enrollment.child_id) {
          studentIds.push({ id: enrollment.user_id, type: 'user' });
        }
        if (enrollment.child_id) {
          studentIds.push({ id: enrollment.child_id, type: 'child' });
        }
      });

      // Get class enrollments for all trial students
      const userIds = studentIds.filter(s => s.type === 'user').map(s => s.id);
      const childIds = studentIds.filter(s => s.type === 'child').map(s => s.id);

      // Get class enrollments for all trial students
      // Query all enrollments and filter by company_id via calendar_events join
      const allStudentIds = [...userIds, ...childIds];
      let allClassEnrollments: any[] = [];
      
      if (allStudentIds.length > 0) {
        // Get class enrollments for all students (users and children)
        // Use inner join to filter by company_id
        const { data: classEnrollments, error: enrollmentsError } = await (supabase as any)
          .from('class_enrollments')
          .select(`
            student_id,
            class_id,
            enrolled_at,
            calendar_events!inner (
              title,
              company_id
            )
          `)
          .in('student_id', allStudentIds)
          .eq('status', 'active')
          .eq('calendar_events.company_id', companyId);
        
        if (!enrollmentsError && classEnrollments) {
          allClassEnrollments = classEnrollments;
        }
      }

      // Group class enrollments by student_id
      const classesByStudent = new Map<string, Array<{ class_id: string; class_name: string; enrolled_at: string }>>();
      allClassEnrollments.forEach(ce => {
        if (!classesByStudent.has(ce.student_id)) {
          classesByStudent.set(ce.student_id, []);
        }
        const classes = classesByStudent.get(ce.student_id);
        if (classes) {
          classes.push({
            class_id: ce.class_id,
            class_name: ce.calendar_events?.title || 'Unnamed Class',
            enrolled_at: ce.enrolled_at,
          });
        }
      });

      // Build trial students array
      const trialStudents: TrialStudent[] = trialEnrollments.map((enrollment: any) => {
        const user = enrollment.user_profiles;
        const child = enrollment.child_profiles;
        const studentId = child ? enrollment.child_id : enrollment.user_id;

        const studentName = child
          ? `${child.first_name || ''} ${child.last_name || ''}`.trim()
          : user
            ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
            : 'Unknown Student';

        const classes = classesByStudent.get(studentId) || [];

        return {
          enrollment_id: enrollment.id,
          user_id: enrollment.user_id,
          child_id: enrollment.child_id,
          student_name: studentName,
          subscription_name: subscriptionNamesMap.get(enrollment.subscription_id) || 'Trial Subscription',
          contract_start_date: enrollment.contract_start_date,
          contract_end_date: enrollment.contract_end_date,
          status: enrollment.status,
          classes,
        };
      });

      // Sort by contract_start_date (newest first)
      return trialStudents.sort((a, b) => {
        const dateA = a.contract_start_date ? new Date(a.contract_start_date).getTime() : 0;
        const dateB = b.contract_start_date ? new Date(b.contract_start_date).getTime() : 0;
        return dateB - dateA;
      });
    },
  });
}
